import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Layout,
  LegacyCard,
  Badge,
  Button,
  Banner,
  Modal,
  SkeletonBodyText,
} from "@shopify/polaris";
import "../style.css";
import "./style.css";
import { useSelector, useDispatch } from "react-redux";
import {
  findByIdApi,
  refreshShopifyProfileApi,
} from "../../../redux/troubleshooting/clientDetails/Action";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import {
  getReIndexStore,
  postCancelDigitalOrder,
  resetCancelDigitalOrder,
  resetReindex,
} from "../../../redux/troubleshooting/reindex/Action";
import {
  cacheSyncForShop,
  resetCacheSync,
} from "../../../redux/troubleshooting/cache-sync/Action";

const IDLE_OPERATION = {
  isRequest: false,
  isSuccess: false,
  isError: false,
  data: null,
  error: null,
  shopId: null,
  requestId: null,
};

const operationForStore = (operation, shopId) => {
  if (
    operation?.shopId === null ||
    operation?.shopId === undefined ||
    shopId === null ||
    shopId === undefined ||
    String(operation.shopId) !== String(shopId)
  ) {
    return IDLE_OPERATION;
  }

  return operation;
};

const responseMessage = (payload, fallback) => {
  if (typeof payload === "string" && payload.trim()) return payload;
  const message = [payload?.message, payload?.detail].find(
    (value) => typeof value === "string" && value.trim()
  );
  return message || fallback;
};

const OperationBanner = ({
  operation,
  successTitle,
  successMessage,
  errorTitle,
  onDismiss,
}) => {
  if (!operation?.isSuccess && !operation?.isError) return null;

  const isError = operation.isError;
  const message = isError
    ? operation.error || "The request failed without an error message."
    : responseMessage(operation.data, successMessage);

  return (
    <div className="mb-1" role="status">
      <Banner
        title={isError ? errorTitle : successTitle}
        status={isError ? "critical" : "success"}
        onDismiss={onDismiss}
      >
        <p>{message}</p>
      </Banner>
    </div>
  );
};

OperationBanner.propTypes = {
  operation: PropTypes.shape({
    isSuccess: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    error: PropTypes.string,
  }),
  successTitle: PropTypes.string.isRequired,
  successMessage: PropTypes.string.isRequired,
  errorTitle: PropTypes.string.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

const hasValue = (value) =>
  value !== null &&
  value !== undefined &&
  !(typeof value === "string" && value.trim() === "");

const displayValue = (value, fallback = "Not recorded") =>
  hasValue(value) ? String(value) : fallback;

const formatDateTime = (value, fallback = "Not recorded") => {
  if (!hasValue(value)) return fallback;
  const date = moment(value);
  if (!date.isValid()) return "Invalid date from source";
  return `${date.fromNow()} · ${date.format("MMM DD, YYYY, h:mm A")}`;
};

const booleanValue = (value, trueLabel = "On", falseLabel = "Off") => {
  if (value === true) return trueLabel;
  if (value === false) return falseLabel;
  return "Unknown";
};

const DetailItem = ({ label, value, hint }) => (
  <div className="store-overview__detail">
    <dt>{label}</dt>
    <dd>{value}</dd>
    {hint ? <small>{hint}</small> : null}
  </div>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
};

const StateBadge = ({
  label,
  value,
  trueLabel = "Available",
  falseLabel,
  falseStatus = "critical",
}) => {
  const known = value === true || value === false;
  const status = value === true ? "success" : value === false ? falseStatus : "new";
  return (
    <Badge status={status}>
      {label}: {known ? (value ? trueLabel : falseLabel) : "Unknown"}
    </Badge>
  );
};

StateBadge.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.bool,
  trueLabel: PropTypes.string,
  falseLabel: PropTypes.string.isRequired,
  falseStatus: PropTypes.string,
};

const ClientDetails = ({
  actionsOnly = false,
  actionsEnabled = true,
  adminActionsEnabled = actionsEnabled,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [submittingAction, setSubmittingAction] = useState(null);
  const [profileRefreshState, setProfileRefreshState] = useState(IDLE_OPERATION);
  const submissionGuard = useRef(null);
  const currentStoreRef = useRef(String(id));
  currentStoreRef.current = String(id);
  const dispatch = useDispatch();
  const storedFindClientsDetails = useSelector(
    (store) => store.requriedFindDetails || IDLE_OPERATION
  );
  const storedReindexState = useSelector(
    (store) => store.requiredReIndexReducer?.reindex || IDLE_OPERATION
  );
  const storedCancelDigitalOrderState = useSelector(
    (store) =>
      store.requiredReIndexReducer?.cancelDigitalOrder || IDLE_OPERATION
  );
  const storedCacheSyncState = useSelector(
    (store) => store.requiredCacheSyncReducer || IDLE_OPERATION
  );
  const storedSupportHealth = useSelector(
    (store) => store.storeSupportHealth || IDLE_OPERATION
  );
  const findClientsDetails = operationForStore(storedFindClientsDetails, id);
  const reindexState = operationForStore(storedReindexState, id);
  const cancelDigitalOrderState = operationForStore(
    storedCancelDigitalOrderState,
    id
  );
  const cacheSyncState = operationForStore(storedCacheSyncState, id);
  const supportHealth =
    storedSupportHealth?.shopId === String(id) && storedSupportHealth?.data
      ? storedSupportHealth.data
      : null;
  const clientDetails =
    findClientsDetails?.data &&
    typeof findClientsDetails.data === "object" &&
    !Array.isArray(findClientsDetails.data)
      ? findClientsDetails.data
      : {};
  const hasCurrentClientDetails = Object.keys(clientDetails).length > 0;
  const safeStoreName =
    typeof clientDetails.name === "string" && clientDetails.name.trim()
      ? clientDetails.name.trim()
      : null;
  const safeStoreDomain =
    typeof clientDetails.domain === "string" && clientDetails.domain.trim()
      ? clientDetails.domain.trim()
      : null;
  const confirmationStoreDetails = [safeStoreName, safeStoreDomain]
    .filter((value, index, values) => value && values.indexOf(value) === index)
    .join(" · ");
  const routeConfirmation =
    confirmationAction?.shopId === String(id) ? confirmationAction : null;

  const currentSubmittingAction =
    submittingAction?.shopId === String(id)
      ? submittingAction.actionName
      : null;
  const reindexPending =
    currentSubmittingAction === "reindex" || reindexState.isRequest;
  const cancelPending =
    currentSubmittingAction === "cancelDigitalOrders" ||
    cancelDigitalOrderState.isRequest;
  const cachePending =
    currentSubmittingAction === "refreshPayPalCache" || cacheSyncState.isRequest;
  const profilePending = currentSubmittingAction === "refreshShopifyProfile";
  const anyActionPending =
    reindexPending || cancelPending || cachePending || profilePending;

  useEffect(() => {
    submissionGuard.current = null;
    setSubmittingAction(null);
    setConfirmationAction(null);
    setProfileRefreshState(IDLE_OPERATION);
    dispatch(resetReindex());
    dispatch(resetCancelDigitalOrder());
    dispatch(resetCacheSync());
    dispatch(findByIdApi(id));
    return () => {
      dispatch(resetReindex());
      dispatch(resetCancelDigitalOrder());
      dispatch(resetCacheSync());
    };
  }, [dispatch, id]);

  const detailsErrorMessage =
    findClientsDetails?.error || findClientsDetails?.data?.message;
  useEffect(() => {
    if (detailsErrorMessage === "Actual error message") navigate("/logout");
  }, [detailsErrorMessage, navigate]);

  const stripHtmlTags = (htmlString) => {
    const doc = new DOMParser().parseFromString(htmlString, "text/html");
    return doc.body.textContent || "";
  };

  const submitAction = async (
    actionName,
    shopId,
    createAction,
    actionAllowed = actionsEnabled
  ) => {
    if (!actionAllowed) return;
    if (String(id) !== shopId) return;
    if (submissionGuard.current?.shopId === shopId || anyActionPending) {
      return;
    }

    const submission = { actionName, shopId };
    submissionGuard.current = submission;
    setSubmittingAction(submission);
    try {
      return await dispatch(createAction(shopId));
    } finally {
      if (submissionGuard.current === submission) {
        submissionGuard.current = null;
        setSubmittingAction(null);
        setConfirmationAction(null);
      }
    }
  };

  const openConfirmation = (actionName) => {
    if (!adminActionsEnabled) return;
    setConfirmationAction(Object.freeze({
      actionName,
      shopId: String(id),
      storeDetails: confirmationStoreDetails,
    }));
  };

  const handleReindexStore = () => {
    if (routeConfirmation?.actionName !== "reindex") return;
    return submitAction(
      "reindex",
      routeConfirmation.shopId,
      getReIndexStore,
      adminActionsEnabled
    );
  };

  const handleCancelDigitalOrders = () => {
    if (routeConfirmation?.actionName !== "cancelDigitalOrders") return;
    return submitAction(
      "cancelDigitalOrders",
      routeConfirmation.shopId,
      postCancelDigitalOrder,
      adminActionsEnabled
    );
  };

  const handleCacheSync = () =>
    submitAction(
      "refreshPayPalCache",
      String(id),
      cacheSyncForShop,
      adminActionsEnabled
    );

  const handleProfileRefresh = async () => {
    if (!actionsEnabled) return;
    const shopId = String(id);
    setProfileRefreshState({
      ...IDLE_OPERATION,
      isRequest: true,
      shopId,
    });
    const result = await submitAction(
      "refreshShopifyProfile",
      shopId,
      refreshShopifyProfileApi
    );
    if (currentStoreRef.current !== shopId || !result) return;

    if (result.ok) {
      setProfileRefreshState({
        ...IDLE_OPERATION,
        isSuccess: true,
        data: result.payload,
        shopId,
      });
      await dispatch(findByIdApi(shopId));
    } else {
      setProfileRefreshState({
        ...IDLE_OPERATION,
        isError: true,
        error: result.error,
        shopId,
      });
    }
  };

  const shopifySessionAvailable =
    supportHealth?.shopifySessionAvailable ?? null;
  const shopifySessionAccessTokenUsable =
    supportHealth?.shopifySessionAccessTokenUsable ?? null;
  const shopifyCredentialPresent =
    supportHealth?.shopifyCredentialPresent ?? null;
  const legacyShopifyCredential =
    shopifySessionAvailable === false && shopifyCredentialPresent === true;
  const subscriptionActive = supportHealth?.subscriptionActive ?? null;
  const activePayPalAccounts = supportHealth?.activePayPalAccounts;
  const paypalConnected =
    typeof activePayPalAccounts === "number"
      ? activePayPalAccounts > 0
      : null;
  const displayDomain =
    clientDetails.myShopifyDomain || clientDetails.domain || null;
  const shopifyAdminUrl =
    typeof displayDomain === "string" &&
    /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(displayDomain)
      ? `https://${displayDomain}/admin`
      : null;
  const country = [clientDetails.countryName, clientDetails.countryCode]
    .filter((value, index, values) => hasValue(value) && values.indexOf(value) === index)
    .join(" · ");
  const accountEmail =
    clientDetails.customerEmail && clientDetails.customerEmail !== clientDetails.email
      ? clientDetails.customerEmail
      : null;
  const deactivationReason = hasValue(clientDetails.deactivationReason)
    ? stripHtmlTags(clientDetails.deactivationReason)
    : clientDetails.state === true
    ? "Not applicable — store is active"
    : "Not recorded";

  return (
    <>
      <Layout.Section>
        <div className="wrapper">
          {findClientsDetails.isError ? (
            <div className="mb-1" role="status">
              <Banner title="Store details could not be loaded" status="critical">
                <p>
                  {findClientsDetails.error ||
                    `The details request for store ID ${id} failed.`}
                </p>
              </Banner>
            </div>
          ) : null}
          <OperationBanner
            operation={reindexState}
            successTitle="Store reindex request submitted"
            successMessage="PalSync accepted the reindex request for this store."
            errorTitle="Store reindex request failed"
            onDismiss={() => dispatch(resetReindex())}
          />
          <OperationBanner
            operation={cancelDigitalOrderState}
            successTitle="Digital-order cancellation submitted"
            successMessage="PalSync accepted the digital-order cancellation request for this store."
            errorTitle="Digital-order cancellation failed"
            onDismiss={() => dispatch(resetCancelDigitalOrder())}
          />
          <OperationBanner
            operation={cacheSyncState}
            successTitle="PayPal cache refreshed"
            successMessage="PalSync refreshed the cached PayPal settings for this store."
            errorTitle="PayPal cache refresh failed"
            onDismiss={() => dispatch(resetCacheSync())}
          />
          <OperationBanner
            operation={profileRefreshState}
            successTitle="Shopify profile refreshed"
            successMessage="Current store identity was loaded from Shopify."
            errorTitle="Shopify profile refresh failed"
            onDismiss={() => setProfileRefreshState(IDLE_OPERATION)}
          />

          <LegacyCard sectioned>
            <div className="store-overview">
              {!actionsOnly ? (
                <>
                  <header className="store-overview__header">
                <div>
                  <p className="store-overview__eyebrow">Store overview</p>
                  <h2>{displayValue(clientDetails.name, "Unnamed store")}</h2>
                  <p className="store-overview__context">
                    <span>{displayValue(displayDomain, "Domain not recorded")}</span>
                    <span>Shopify ID {displayValue(clientDetails.id || id, "Unknown")}</span>
                  </p>
                </div>
                <div className="store-overview__badges" aria-label="Store status">
                  <StateBadge
                    label="Shopify app"
                    value={clientDetails.state}
                    trueLabel="Active"
                    falseLabel="Inactive"
                  />
                  <Badge
                    status={
                      shopifySessionAvailable === true
                        ? "success"
                        : legacyShopifyCredential
                        ? "warning"
                        : shopifySessionAvailable === false
                        ? "critical"
                        : "new"
                    }
                  >
                    Shopify session: {shopifySessionAvailable === true
                      ? "Recoverable"
                      : legacyShopifyCredential
                      ? "Legacy token only"
                      : shopifySessionAvailable === false
                      ? "Session required"
                      : "Unknown"}
                  </Badge>
                  <Badge
                      status={
                        shopifySessionAccessTokenUsable === true
                          ? "success"
                          : shopifyCredentialPresent === false
                          ? "critical"
                          : shopifySessionAvailable === true
                          ? "info"
                          : "warning"
                    }
                  >
                    Shopify API: {shopifySessionAccessTokenUsable === true
                      ? "Ready"
                      : legacyShopifyCredential
                      ? "Legacy token stored"
                      : shopifyCredentialPresent === false
                      ? "Unavailable"
                      : shopifySessionAvailable === true
                      ? "Auto-renewal available"
                      : "Unknown"}
                  </Badge>
                  <StateBadge
                    label="PayPal"
                    value={paypalConnected}
                    trueLabel="Connected"
                    falseLabel="Not connected"
                  />
                  <StateBadge
                    label="Subscription"
                    value={subscriptionActive}
                    trueLabel="Active"
                    falseLabel="Inactive"
                    falseStatus="warning"
                  />
                  <Badge>{displayValue(clientDetails.plan, "Plan unknown")}</Badge>
                </div>
                  </header>

                  {!hasCurrentClientDetails && findClientsDetails.isRequest ? (
                    <div className="store-overview__loading" aria-label="Loading store overview">
                      <SkeletonBodyText lines={8} />
                    </div>
                  ) : (
                    <div className="store-overview__grid">
                  <section className="store-overview__panel" aria-labelledby="identity-heading">
                    <h3 id="identity-heading">Store identity</h3>
                    <dl>
                      <DetailItem
                        label="Owner"
                        value={displayValue(
                          clientDetails.shopOwner,
                          "Not captured — refresh Shopify profile"
                        )}
                      />
                      <DetailItem label="Contact email" value={displayValue(clientDetails.email)} />
                      {accountEmail ? (
                        <DetailItem label="Shopify account email" value={accountEmail} />
                      ) : null}
                      <DetailItem label="Country" value={displayValue(country)} />
                      <DetailItem label="Currency" value={displayValue(clientDetails.currency)} />
                      <DetailItem label="Shopify plan" value={displayValue(clientDetails.shopifyPlan)} />
                    </dl>
                  </section>

                  <section className="store-overview__panel" aria-labelledby="account-heading">
                    <h3 id="account-heading">PalSync account</h3>
                    <dl>
                      <DetailItem label="PalSync plan" value={displayValue(clientDetails.plan)} />
                      <DetailItem
                        label="Subscription"
                        value={booleanValue(subscriptionActive, "Active", "Inactive")}
                      />
                      <DetailItem
                        label="Subscription accepted"
                        value={formatDateTime(
                          clientDetails.chargeFirstAcceptedAt,
                          subscriptionActive === false ? "No active subscription" : "Not recorded"
                        )}
                      />
                      <DetailItem
                        label="Historical imports remaining"
                        value={displayValue(clientDetails.totalTokens, "Unknown")}
                        hint="Legacy historical-import request allowance."
                      />
                      <DetailItem
                        label="Onboarding"
                        value={booleanValue(clientDetails.onBoarded, "Completed", "Not completed")}
                      />
                      <DetailItem label="Pull mode" value={displayValue(clientDetails.pullType)} />
                    </dl>
                  </section>

                  <section className="store-overview__panel" aria-labelledby="configuration-heading">
                    <h3 id="configuration-heading">Sync configuration</h3>
                    <dl>
                      <DetailItem
                        label="Order sync"
                        value={booleanValue(clientDetails.syncEnabled, "Enabled", "Disabled")}
                      />
                      <DetailItem
                        label="Digital orders"
                        value={booleanValue(clientDetails.digitalSync)}
                      />
                      <DetailItem
                        label="Courier mapping"
                        value={booleanValue(clientDetails.courrierMapping)}
                      />
                      <DetailItem
                        label="Courier matcher"
                        value={booleanValue(clientDetails.courrierMatcher)}
                      />
                      <DetailItem
                        label="Webhook check"
                        value={booleanValue(clientDetails.webhookCheck, "Passed", "Not verified")}
                      />
                      <DetailItem
                        label="Webhook status"
                        value={displayValue(clientDetails.webhookStatus)}
                      />
                      <DetailItem
                        label="Checkout API support"
                        value={booleanValue(
                          clientDetails.checkoutApiSupported,
                          "Supported",
                          "Not supported"
                        )}
                      />
                    </dl>
                  </section>

                  <section className="store-overview__panel" aria-labelledby="activity-heading">
                    <h3 id="activity-heading">Lifecycle and activity</h3>
                    <dl>
                      <DetailItem
                        label="Installed in PalSync"
                        value={formatDateTime(clientDetails.creationDate)}
                      />
                      <DetailItem
                        label="Shopify store created"
                        value={formatDateTime(
                          clientDetails.createdAt,
                          "Not captured — refresh Shopify profile"
                        )}
                      />
                      <DetailItem
                        label="Shopify profile updated"
                        value={formatDateTime(clientDetails.updatedAt)}
                      />
                      <DetailItem
                        label="Last order import"
                        value={formatDateTime(clientDetails.lastImportAt, "No import recorded")}
                      />
                      <DetailItem
                        label="Last pull processed"
                        value={formatDateTime(
                          clientDetails.lastProcessedAtPullAt,
                          "No pull processing recorded"
                        )}
                      />
                      <DetailItem
                        label="Last pull status"
                        value={displayValue(clientDetails.lastPullStatus)}
                      />
                      <DetailItem
                        label="Last pull error"
                        value={formatDateTime(
                          clientDetails.lastPullErrorAt,
                          "No pull error recorded"
                        )}
                      />
                      <DetailItem
                        label="Last successful PayPal sync"
                        value={formatDateTime(clientDetails.lastSyncAt, "No successful sync recorded")}
                      />
                      <DetailItem
                        label="Import paused since"
                        value={
                          clientDetails.state === true
                            ? "Not applicable — store is active"
                            : formatDateTime(clientDetails.deactivatedAt)
                        }
                      />
                      <DetailItem label="Deactivation reason" value={deactivationReason} />
                    </dl>
                  </section>
                    </div>
                  )}
                </>
              ) : null}

              <section className="support-tools" aria-labelledby="support-tools-heading">
                <div className="support-tools__heading">
                  <div>
                    <p className="store-overview__eyebrow">Support tools</p>
                    <h3 id="support-tools-heading">Investigate first, then change state</h3>
                  </div>
                  <p>Actions are scoped to Shopify ID {id}.</p>
                </div>
                <div className="support-tools__grid" aria-label="Support account actions">
                  <div role="group" aria-labelledby="external-tools-heading">
                    <h4 id="external-tools-heading">External reference</h4>
                    <p>Open the corresponding Partner store record.</p>
                    {hasCurrentClientDetails && shopifyAdminUrl ? (
                      <a
                        className="linkContainer"
                        target="_blank"
                        rel="noreferrer"
                        href={shopifyAdminUrl}
                      >
                        <Button>View in Shopify</Button>
                      </a>
                    ) : (
                      <Button disabled>View in Shopify</Button>
                    )}
                  </div>

                  <div role="group" aria-labelledby="cache-actions-heading">
                    <h4 id="cache-actions-heading">Cache maintenance</h4>
                    <p>
                      Fetch current Shopify identity or reload PayPal configuration.
                      {shopifySessionAvailable === true &&
                      shopifySessionAccessTokenUsable === false
                        ? " Core processing can renew this Shopify token automatically; retry profile refresh afterward."
                        : ""}
                    </p>
                    <div className="support-tools__buttons">
                      <Button
                        onClick={handleProfileRefresh}
                        loading={profilePending}
                        disabled={
                          anyActionPending ||
                          !actionsEnabled ||
                          supportHealth?.shopifyCredentialPresent === false ||
                          (supportHealth?.shopifySessionAvailable === true &&
                            supportHealth?.shopifySessionAccessTokenUsable === false)
                        }
                      >
                        Refresh Shopify profile
                      </Button>
                      <Button
                        onClick={handleCacheSync}
                        loading={cachePending}
                        disabled={anyActionPending || !adminActionsEnabled}
                      >
                        Refresh PayPal cache
                      </Button>
                    </div>
                  </div>

                  <div
                    className="support-tools__danger"
                    role="group"
                    aria-labelledby="store-actions-heading"
                  >
                    <h4 id="store-actions-heading">Store data actions</h4>
                    <p>These commands can enqueue or stop background work.</p>
                    <div className="support-tools__buttons">
                      <Button
                        onClick={() => openConfirmation("reindex")}
                        loading={reindexPending}
                        disabled={anyActionPending || !adminActionsEnabled}
                      >
                        Reindex store
                      </Button>
                      <Button
                        onClick={() => openConfirmation("cancelDigitalOrders")}
                        loading={cancelPending}
                        disabled={anyActionPending || !adminActionsEnabled}
                        destructive
                      >
                        Cancel digital orders
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </LegacyCard>
        </div>
      </Layout.Section>
      <Modal
        open={routeConfirmation?.actionName === "reindex"}
        onClose={() => {
          if (!reindexPending) setConfirmationAction(null);
        }}
        title="Confirm store reindex"
        primaryAction={{
          content: "Confirm reindex",
          onAction: handleReindexStore,
          loading: reindexPending,
          disabled: anyActionPending || !adminActionsEnabled,
        }}
        secondaryActions={[
          {
            content: "Keep store unchanged",
            onAction: () => setConfirmationAction(null),
            disabled: reindexPending,
          },
        ]}
      >
        <Modal.Section>
          <p>
            Target: <strong>Store ID {routeConfirmation?.shopId}</strong>
            {routeConfirmation?.storeDetails
              ? ` — ${routeConfirmation.storeDetails}`
              : null}
          </p>
          <p>
            This submits a full reindex request for this PalSync store. It can
            enqueue substantial background work and temporarily increase
            processing load.
          </p>
          <p>
            It does not refresh PayPal settings or cancel digital-order work.
            Verify that reindexing is required before continuing.
          </p>
        </Modal.Section>
      </Modal>
      <Modal
        open={routeConfirmation?.actionName === "cancelDigitalOrders"}
        onClose={() => {
          if (!cancelPending) setConfirmationAction(null);
        }}
        title="Confirm digital-order cancellation"
        primaryAction={{
          content: "Confirm cancellation",
          onAction: handleCancelDigitalOrders,
          loading: cancelPending,
          disabled: anyActionPending || !adminActionsEnabled,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: "Keep digital orders running",
            onAction: () => setConfirmationAction(null),
            disabled: cancelPending,
          },
        ]}
      >
        <Modal.Section>
          <p>
            Target: <strong>Store ID {routeConfirmation?.shopId}</strong>
            {routeConfirmation?.storeDetails
              ? ` — ${routeConfirmation.storeDetails}`
              : null}
          </p>
          <p>
            This sends a cancellation request for digital-order processing for
            this store. It can stop pending PalSync work and cannot be undone
            from this screen.
          </p>
          <p>
            Confirm the affected store and merchant impact before continuing.
            This action does not reindex the store or refresh PayPal settings.
          </p>
        </Modal.Section>
      </Modal>
    </>
  );
};

ClientDetails.propTypes = {
  actionsOnly: PropTypes.bool,
  actionsEnabled: PropTypes.bool,
  adminActionsEnabled: PropTypes.bool,
};

export default ClientDetails;
